import apiEndPoints from "../../apiconstants";
import { api, handleErrorResponse, responseArgs } from "../api";

export interface ConfigurationQueryPayload {
    scope: string;
    pagination: {
        page: number;
        per_page: number;
    };
    filters?: {
        aiworker_id: string;
        status?: string;
        is_active?: boolean;
        config_name?: string;
        config_id?: string;
        configuration_id?: string;
        version?: number | string;
        version_id?: string;
    };
    aiworker_id?: string;
    config_name?: string;
    config_id?: string;
    configuration_id?: string;
    version?: number | string;
    version_id?: string;
}

export interface AgentConfigNestedSection {
    configuration_id?: string;
    config?: Record<string, unknown>;
}

export interface AgentConfigAggregate {
    bot_data?: AgentConfigNestedSection;
    dify_config?: AgentConfigNestedSection;
    workflow_data?: AgentConfigNestedSection;
}


export interface AiWorkerConfigurationQueryResponse {
    page: number;
    per_page: number;
    total: number;
    configurations: Array<{
        configuration_id?: string;
        aiworker_id?: string;
        config_id?: string;
        config_name?: string;
        name?: string;
        category?: string;
        version?: number;
        status?: string;
        is_active?: boolean;
        config?: AgentConfigAggregate;
    } & Record<string, unknown>>;
}

export interface UpdateAiWorkerConfigurationPayload {
    configurationId: string;
    payload: Record<string, unknown>;
}

export interface CreateAiWorkerConfigurationPayload {
    aiworkerId: string;
    agentName: string;
    configId: string;
    config: Record<string, unknown>;
}

export interface AiWorkerUsageTag {
    key: string;
    value: string;
}

export interface CreateAiWorkerUsagePayload {
    aiworker_id: string;
    aiworker_type: string;
    used_at: string;
    status: string;
    tenant_id: string;
    executed_by: string;
    message_id: string;
    aiworker_usage_id: string;
    input_data: Record<string, unknown>;
    tags: AiWorkerUsageTag[];
}

export interface UpdateAiWorkerUsagePayload {
    aiworker_usage_id: string;
    body: Record<string, unknown>;
}

export const agentApiSlice = api.injectEndpoints({
    endpoints: builder => ({
        getPDFUploadPresignedUrl: builder.mutation<string, any>({
            query: (payLoad) => ({
                url: apiEndPoints.getPDFPresignedURL,
                method: 'POST',
                body: payLoad
            }),
            transformResponse: (response: responseArgs) => response.data?.[0],
            transformErrorResponse: handleErrorResponse,
        }),
        getAgentsJSON: builder.mutation<any, any>({
            query: (payLoad) => ({
                url: apiEndPoints.getAgentsJSON,
                method: 'POST',
                body: payLoad
            }),
            transformResponse: (response: any) => response.data,
            transformErrorResponse: handleErrorResponse
        }),
        repairProfile: builder.mutation<any, any>({
            queryFn: async (payLoad: any) => {
                const response = await fetch(`https://uf1mhp7sjh.execute-api.us-east-1.amazonaws.com/dev/defect`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'x-api-key': 'wMUG1SZN8B1yUs8Y0odNL9PJOCvOa7Dx7o1ytBaT'
                    },
                    mode: 'cors',
                    body: JSON.stringify(payLoad)
                });

                if (!response.ok) {
                    throw new Error('Failed to fetch');
                }

                const data = await response.json();                                                                                                                                     
                return { data: data };
            }
        }),
        generatePresignedUrl: builder.mutation<{ presigned_url: string, unique_id: string; }, { file_name: string; }>({
            query: (payLoad) => ({
                url: apiEndPoints.getPresignedURl,
                method: 'POST',
                body: payLoad
            }),
            transformResponse: (response: responseArgs) => response.data,
            transformErrorResponse: handleErrorResponse,
        }),
        getClaimScoreTableData: builder.mutation<string, any>({
            query: (payLoad) => ({
                url: apiEndPoints.getClaimsScore,
                method: 'POST',
                body: payLoad
            }),
            transformResponse: (response: responseArgs) => response.data,
            transformErrorResponse: handleErrorResponse,
        }),
        uploadAgentImage: builder.mutation<string, any>({
            query: (payLoad) => ({
                url: apiEndPoints.uploadAgentImage,
                method: 'POST',
                body: payLoad
            }),
            transformResponse: (response: responseArgs) => response.data,
            transformErrorResponse: handleErrorResponse,
        }),
        getImageModalData: builder.mutation<string, any>({
            query: (payLoad) => ({
                url: apiEndPoints.getImageModalData,
                method: 'POST',
                body: payLoad
            }),
            transformErrorResponse: handleErrorResponse,
            transformResponse: (response: responseArgs) => response.data,
        }),
        agentTicketCreation: builder.mutation<any, any>({
            query: (payLoad) => ({
                url: apiEndPoints.agentTicketCreation,
                method: 'POST',
                body: payLoad
            }),
            transformResponse: (response: responseArgs) => response.data,
            transformErrorResponse: handleErrorResponse,
        }),
        uploadAiWorkerSources: builder.mutation<any, { payLoad: any, source_id: string}>({
            query: ({ source_id, payLoad}) => ({
                url: `${apiEndPoints.uploadAiWorkerSources}/${source_id}`,
                method: 'POST',
                body: payLoad
            }),
            transformResponse: (response: responseArgs) => response.data,
            transformErrorResponse: handleErrorResponse,
        }),
        uploadAgentKnowledge: builder.mutation<any, string>({
            query: (payLoad) => ({
                url: `${apiEndPoints.agentFileUpload}?source_type=${payLoad}`,
                method: 'GET'
            }),
            transformResponse: (response: responseArgs) => response.data,
            transformErrorResponse: handleErrorResponse,
        }),
        uploadAgentTool: builder.mutation<any, any>({
            query: ({ url, body }) => ({
                url: `${url}`,
                method: 'POST',
                body: body
            }),
            transformResponse: (response: responseArgs) => response.data,
            transformErrorResponse: handleErrorResponse,
        }),
        getTagsData: builder.mutation<any, { key: string; value: string }>({
            query: ({ key, value }) => ({
                url: `${apiEndPoints.getTags}?key=${key}&value=${value}`,
                method: 'GET'
            }),
            transformResponse: (response: responseArgs) => response.data,
            transformErrorResponse: handleErrorResponse,
        }),
        getAiWorkersUsageEvents: builder.mutation<any, string>({
            query: (aiworker_usage_id) => ({
                url: `${apiEndPoints.getAiWorkersUsageEvents}?aiworker_usage_id=${aiworker_usage_id}`,
                method: 'GET'
            }),
            transformResponse: (response: responseArgs) => response.data,
            transformErrorResponse: handleErrorResponse,
        }),
        createAiWorkerUsage: builder.mutation<any, CreateAiWorkerUsagePayload>({
            query: (payload) => ({
                url: apiEndPoints.getAiWorkersUsageByCategory,
                method: 'POST',
                body: payload
            }),
            transformResponse: (response: any) => response?.data ?? response,
            transformErrorResponse: handleErrorResponse,
        }),
        updateAiWorkerUsage: builder.mutation<any, UpdateAiWorkerUsagePayload>({
            query: ({ aiworker_usage_id, body }) => ({
                url: `${apiEndPoints.getAiWorkersUsageByCategory}?aiworker_usage_id=${encodeURIComponent(aiworker_usage_id)}`,
                method: 'PATCH',
                body
            }),
            transformResponse: (response: any) => response?.data ?? response,
            transformErrorResponse: handleErrorResponse,
        }),
        getAiWorkerConfigurations: builder.query<AiWorkerConfigurationQueryResponse, ConfigurationQueryPayload>({
            query: (payload) => ({
                url: apiEndPoints.configurationManagementQuery,
                method: 'POST',
                body: payload
            }),
            transformResponse: (response: any) => response?.data ?? {
                page: 1,
                per_page: 10,
                total: 0,
                configurations: []
            },
            transformErrorResponse: handleErrorResponse,
        }),
        getAgentConfigurationVersions: builder.mutation<any, { aiworker_id: string; page?: number; per_page?: number }>({
            query: ({ aiworker_id, page = 1, per_page = 20 }) => ({
                url: `${apiEndPoints.configurationManagement}/versions`,
                method: 'POST',
                body: {
                    scope: 'tenant',
                    aiworker_id,
                    config_name: 'agent_config',
                    pagination: { page, per_page },
                }
            }),
            transformErrorResponse: handleErrorResponse,
            transformResponse: (response: any) => response?.data,
        }),
        createAiWorkerConfiguration: builder.mutation<any, CreateAiWorkerConfigurationPayload>({
            query: ({ aiworkerId, agentName, configId, config }) => ({
                url: apiEndPoints.configurationManagement,
                method: 'POST',
                body: {
                    scope: 'tenant',
                    aiworker_id: aiworkerId,
                    name: agentName,
                    category: 'aiworker',
                    sub_category: 'agent',
                    config_name: 'agent_config',
                    config_id: configId,
                    config,
                    type: 'default',
                    status: 'active',
                    is_active: true,
                },
            }),
            transformResponse: (response: any) => response?.data ?? response,
            transformErrorResponse: handleErrorResponse,
        }),
        updateAiWorkerConfiguration: builder.mutation<any, UpdateAiWorkerConfigurationPayload>({
            query: ({ configurationId, payload }) => ({
                url: `${apiEndPoints.configurationManagement}/${configurationId}`,
                method: 'PATCH',
                body: payload
            }),
            transformResponse: (response: any) => response?.data ?? response,
            transformErrorResponse: handleErrorResponse,
        }),
        rollbackAgentConfigurationVersion: builder.mutation<any, { config_id: string; version_id: string; config_name?: string }>({
            query: ({ config_id, version_id, config_name = 'agent_config' }) => ({
                url: `${apiEndPoints.configurationManagement}/rollback`,
                method: 'PATCH',
                body: {
                    scope: 'tenant',
                    config_id,
                    config_name,
                    version_id,
                }
            }),
            transformErrorResponse: handleErrorResponse,
        })
    })
});

export const { useGetPDFUploadPresignedUrlMutation, useGetAgentsJSONMutation, useRepairProfileMutation, useGeneratePresignedUrlMutation, useGetClaimScoreTableDataMutation, useUploadAgentImageMutation, useGetImageModalDataMutation, useAgentTicketCreationMutation, useUploadAiWorkerSourcesMutation, useUploadAgentKnowledgeMutation, useUploadAgentToolMutation, useGetTagsDataMutation, useGetAiWorkersUsageEventsMutation, useCreateAiWorkerUsageMutation, useUpdateAiWorkerUsageMutation, useGetAiWorkerConfigurationsQuery, useLazyGetAiWorkerConfigurationsQuery, useGetAgentConfigurationVersionsMutation, useCreateAiWorkerConfigurationMutation, useUpdateAiWorkerConfigurationMutation, useRollbackAgentConfigurationVersionMutation } = agentApiSlice;